import React, { useState, useEffect } from 'react';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import filter_icon from '../../assets/images/filter_icon.png';
import usersIcon from '../../assets/images/users_icon.png';
import productsIcon from '../../assets/images/products_icon.png';
import ordersIcon from '../../assets/images/orders_icon.png';
import search_icon from '../../assets/images/search_icon.png';
import eyeIcon from '../../assets/images/eyeIcon.png';
import brownfilter_icon from '../../assets/images/brownfilter_icon.png';
import Addproduct from '../Addproduct';
import OrderDetailsModal from "../OrderDetailsModal";
import AddSelfOrderModal from "../AddSelfOrderModal";

import { GetAllOrder, GetDashboardCounts, UpdateOrderStatus } from "../../Services/OrderService";
import api from "../../config/axios";
import endpoints from "../../config/endpoints";

const ORDER_STATUS = {
  new: 1,
  inProgress: 2,
  outForDelivery: 3,
  delivered: 4,
};

// Get logged-in user name from localStorage
const getLoggedInUserName = () => {
  try {
    const loginDetail = localStorage.getItem("loginDetail");
    if (loginDetail) {
      return JSON.parse(loginDetail).name || "Admin";
    }
  } catch (e) {}
  return "Admin";
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [showPopup, setShowPopup] = useState(false);
  const [showSelfOrderModal, setShowSelfOrderModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [currentOrders, setCurrentOrders] = useState([]);
  const [allMergedCount, setAllMergedCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const userName = getLoggedInUserName();
  const userRole = localStorage.getItem("userRole");
  const vendorId = userRole === "vendor" ? localStorage.getItem("userId") : null;

  // Date Filtering State
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [activeStartDate, setActiveStartDate] = useState('');
  const [activeEndDate, setActiveEndDate] = useState('');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [counts, setCounts] = useState({
    userCount: 0,
    productCount: 0,
    orderCount: 0,
  });

  const [tableTabCount, settableTabCount] = useState({
    allOrdersCount: 0,
    websiteOrdersCount: 0,
    whatsAppOrdersCount: 0,
    newOrdersCount: 0,
    inProgressCount: 0,
    outForDeliveryCount: 0,
    deliveredCount: 0,
  });

  const [salesTrend, setSalesTrend] = useState([]);
  const [chartFilter, setChartFilter] = useState('both'); // 'both' | 'website' | 'whatsapp'

  // Calculate Dates for Filtering
  const getDatesForFilter = (filter, customStart, customEnd) => {
    const today = new Date();
    let start = '';
    let end = '';

    if (filter === 'today') {
      start = today.toLocaleDateString('sv-SE');
      end = today.toLocaleDateString('sv-SE');
    } else if (filter === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      start = yesterday.toLocaleDateString('sv-SE');
      end = yesterday.toLocaleDateString('sv-SE');
    } else if (filter === 'thisWeek') {
      const currentDay = today.getDay(); // 0 is Sunday, 1 is Monday
      const distance = currentDay === 0 ? -6 : 1 - currentDay;
      const monday = new Date();
      monday.setDate(today.getDate() + distance);
      start = monday.toLocaleDateString('sv-SE');
      end = today.toLocaleDateString('sv-SE');
    } else if (filter === 'custom') {
      start = customStart;
      end = customEnd;
    }
    return { start, end };
  };

  useEffect(() => {
    const { start, end } = getDatesForFilter(dateFilter, customStartDate, customEndDate);
    setActiveStartDate(start);
    setActiveEndDate(end);
  }, [dateFilter, customStartDate, customEndDate]);

  const loadDashboardCounts = async () => {
    try {
      const res = await GetDashboardCounts(vendorId, activeStartDate, activeEndDate);
      if (res?.status) {
        setCounts(res.data);
      }
    } catch (err) {
      console.error("Error loading dashboard counts:", err);
    }
  };

  // Helper: parse sheet date to "YYYY-MM-DD"
  // Handles: "YYYY-MM-DD HH:MM:SS", "YYYY-MM-DD", "DD-MM-YYYY"
  const parseSheetDate = (dateStr) => {
    if (!dateStr) return null;
    const s = dateStr.toString().trim();
    // Handle datetime: "2026-03-25 17:00:53" -> take first 10 chars
    const datePart = s.length >= 10 ? s.substring(0, 10) : s;
    const parts = datePart.split(/[-/]/);
    if (parts.length !== 3) return null;
    if (parts[0].length === 4) return datePart; // YYYY-MM-DD
    // DD-MM-YYYY
    return `${parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`;
  };

  const loadSalesTrend = async () => {
    try {
      const today = new Date();
      let startD = new Date();
      let endD = new Date();

      if (activeStartDate && activeEndDate) {
        startD = new Date(activeStartDate);
        endD = new Date(activeEndDate);
      } else {
        startD.setDate(today.getDate() - 6);
      }

      const startDateStr = startD.toLocaleDateString('sv-SE');
      const endDateStr = endD.toLocaleDateString('sv-SE');

      const params = { startDate: startDateStr, endDate: endDateStr };
      if (vendorId) {
        params.vendorId = vendorId;
      }

      // Use allSettled so sheet API failure doesn't break DB orders
      const [dbResult, sheetResult] = await Promise.allSettled([
        GetAllOrder(params),
        api.get(`${endpoints.WhatsApp.GetSheetOrders}?pageSize=1000`)
      ]);
      const dbRes = dbResult.status === 'fulfilled' ? dbResult.value : null;
      const sheetRes = sheetResult.status === 'fulfilled' ? sheetResult.value : null;

      if (dbRes?.status) {
        const orders = dbRes.data.orders || [];
        const sheetOrders = sheetRes?.data?.status ? (sheetRes.data.data || []) : [];
        const daysData = [];
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        const diffTime = Math.abs(endD - startD);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        const barLimit = Math.min(diffDays, 30);

        for (let i = barLimit - 1; i >= 0; i--) {
          const d = new Date(endD);
          d.setDate(endD.getDate() - i);
          const dateStr = d.toLocaleDateString('sv-SE'); // YYYY-MM-DD
          const label = dayNames[d.getDay()];
          const displayDate = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

          // Website (DB) orders for this day (non-WhatsApp)
          const dayWebsiteOrders = orders.filter(o => {
            const oDate = new Date(o.createdOnUtc).toLocaleDateString('sv-SE');
            const status = o.orderStatus?.toLowerCase();
            const isValid = status !== "cancelled" && status !== "failed" && status !== "refunded";
            const src = (o.orderSource || '').toLowerCase();
            const pm = (o.paymentMethod || '').toLowerCase();
            const isWebsite = src === 'website' || src === 'app' || (src !== 'whatsapp' && pm !== 'whatsapp');
            return oDate === dateStr && isValid && isWebsite;
          });
          const totalSales = dayWebsiteOrders.reduce((sum, o) => sum + (o.totalPrice || 0), 0);

          // WhatsApp DB orders for this day
          const dayWpDbOrders = orders.filter(o => {
            const oDate = new Date(o.createdOnUtc).toLocaleDateString('sv-SE');
            const status = o.orderStatus?.toLowerCase();
            const isValid = status !== "cancelled" && status !== "failed" && status !== "refunded";
            const src = (o.orderSource || '').toLowerCase();
            const pm = (o.paymentMethod || '').toLowerCase();
            const isWp = src === 'whatsapp' || pm === 'whatsapp';
            return oDate === dateStr && isValid && isWp;
          });
          const wpDbTotal = dayWpDbOrders.reduce((sum, o) => sum + (o.totalPrice || 0), 0);

          // WhatsApp (Sheet) orders for this day
          const wpSheetOrders = sheetOrders.filter(row => {
            const parsed = parseSheetDate(row.date);
            return parsed === dateStr;
          });
          const wpSheetTotal = wpSheetOrders.reduce((sum, row) => {
            const amt = Number((row.total || '').toString().replace(/[^0-9.]/g, ''));
            return sum + (isNaN(amt) ? 0 : amt);
          }, 0);

          const wpTotal = Math.max(wpDbTotal, wpSheetTotal);

          daysData.push({
            day: label,
            date: displayDate,
            value: totalSales,
            wpValue: wpTotal
          });
        }
        setSalesTrend(daysData);
      }
    } catch (err) {
      console.error("Error loading sales trend:", err);
    }
  };

  // Helper to match Google Sheet row status to dashboard tabs
  const matchSheetStatusToTab = (sheetStatus, tabStatus) => {
    if (!sheetStatus) {
      return tabStatus === 'new';
    }
    const s = sheetStatus.toString().toLowerCase().trim();
    if (tabStatus === 'new') {
      return s === 'pending' || s === 'new' || s === '1';
    }
    if (tabStatus === 'inProgress') {
      return s === 'processing' || s === 'inprogress' || s === 'in progress' || s === '2';
    }
    if (tabStatus === 'outForDelivery') {
      return s === 'outfordelivery' || s === 'out for delivery' || s === '3';
    }
    if (tabStatus === 'delivered') {
      return s === 'delivered' || s === 'completed' || s === '4';
    }
    return false;
  };

  const loadOrders = async (status, term = "") => {
    try {
      setIsLoadingOrders(true);
      setCurrentOrders([]);

      // Prepare DB params for orders matching date filter & vendor
      const dbParams = { pageIndex: 0, pageSize: 1000 };
      if (activeStartDate) dbParams.startDate = activeStartDate;
      if (activeEndDate) dbParams.endDate = activeEndDate;
      if (vendorId) dbParams.vendorId = vendorId;

      // Use allSettled so sheet API failure doesn't break DB orders
      const [dbResult2, sheetResult2] = await Promise.allSettled([
        GetAllOrder(dbParams),
        api.get(`${endpoints.WhatsApp.GetSheetOrders}?pageSize=1000`)
      ]);
      const dbRes = dbResult2.status === 'fulfilled' ? dbResult2.value : null;
      const sheetRes = sheetResult2.status === 'fulfilled' ? sheetResult2.value : null;

      if (dbRes?.status) {
        // 1. All DB orders from SQL Database (filtering out orphan 0-price stubs with no products)
        const rawDbOrders = dbRes.data.orders || [];
        const allDbOrders = rawDbOrders.filter(o => (o.products && o.products.length > 0) || (o.totalPrice && o.totalPrice > 0));

        // 2. Google Sheet WhatsApp orders (mapped & date-filtered) — fallback to [] if sheet fails
        const rawSheetOrders = (sheetRes?.data?.status && sheetRes?.data?.data) ? sheetRes.data.data : [];
        let sheetOrders = rawSheetOrders.map(row => mapSheetOrderToOrder(row));

        if (activeStartDate && activeEndDate) {
          const start = new Date(activeStartDate);
          const end = new Date(activeEndDate);
          end.setHours(23, 59, 59, 999);
          sheetOrders = sheetOrders.filter(o => {
            const d = new Date(o.createdOnUtc);
            return d >= start && d <= end;
          });
        }

        // Helper to match DB order status
        const matchDbStatus = (orderStatus, tab) => {
          const s = (orderStatus || '').toLowerCase();
          if (tab === 'new') return s === 'pending' || s === 'new';
          if (tab === 'inProgress') return s === 'processing' || s === 'in progress' || s === 'inprogress';
          if (tab === 'outForDelivery') return s === 'outfordelivery' || s === 'out for delivery';
          if (tab === 'delivered') return s === 'completed' || s === 'delivered';
          return false;
        };

        // Helper to combine DB & Sheet orders with deduplication
        const combineDedupe = (dbArr, sheetArr) => {
          const map = new Map();
          dbArr.forEach(o => map.set(String(o.orderId || o.id), o));
          sheetArr.forEach(o => {
            const key = String(o.orderId || o.id);
            if (!map.has(key)) {
              map.set(key, o);
            }
          });
          return Array.from(map.values());
        };

        // Categorize DB & Sheet orders: Website = DB website orders, WhatsApp = Google Sheet orders
        const websiteOrdersCombined = allDbOrders.filter(o => 
          (o.orderSource || '').toLowerCase() === 'website' || 
          (o.orderSource || '').toLowerCase() === 'app' || 
          ((o.orderSource || '').toLowerCase() !== 'whatsapp' && (o.paymentMethod || '').toLowerCase() !== 'whatsapp')
        );
        const dbWhatsAppOrders = allDbOrders.filter(o => 
          (o.orderSource || '').toLowerCase() === 'whatsapp' || 
          (o.paymentMethod || '').toLowerCase() === 'whatsapp'
        );
        // If Google Sheet orders exist, use Sheet orders for WhatsApp tab (183 orders); fallback to DB WhatsApp orders if sheet fails
        const whatsAppOrdersCombined = sheetOrders.length > 0 ? sheetOrders : dbWhatsAppOrders;
        const allOrdersCombined = combineDedupe(websiteOrdersCombined, whatsAppOrdersCombined);
        const pendingOrders = combineDedupe(
          allDbOrders.filter(o => matchDbStatus(o.orderStatus, 'new')),
          sheetOrders.filter(o => matchSheetStatusToTab(o.orderStatus, 'new'))
        );
        const inProgressOrders = combineDedupe(
          allDbOrders.filter(o => matchDbStatus(o.orderStatus, 'inProgress')),
          sheetOrders.filter(o => matchSheetStatusToTab(o.orderStatus, 'inProgress'))
        );
        const outForDeliveryOrders = combineDedupe(
          allDbOrders.filter(o => matchDbStatus(o.orderStatus, 'outForDelivery')),
          sheetOrders.filter(o => matchSheetStatusToTab(o.orderStatus, 'outForDelivery'))
        );
        const deliveredOrders = combineDedupe(
          allDbOrders.filter(o => matchDbStatus(o.orderStatus, 'delivered')),
          sheetOrders.filter(o => matchSheetStatusToTab(o.orderStatus, 'delivered'))
        );

        // 3. Compute accurate counts for ALL tab badges
        settableTabCount({
          allOrdersCount: allOrdersCombined.length,
          websiteOrdersCount: websiteOrdersCombined.length,
          whatsAppOrdersCount: whatsAppOrdersCombined.length,
          newOrdersCount: pendingOrders.length,
          inProgressCount: inProgressOrders.length,
          outForDeliveryCount: outForDeliveryOrders.length,
          deliveredCount: deliveredOrders.length,
        });

        // 4. Select merged orders for current active tab
        let targetOrders = [];
        if (status === 'all') {
          targetOrders = allOrdersCombined;
        } else if (status === 'website') {
          targetOrders = websiteOrdersCombined;
        } else if (status === 'whatsapp') {
          targetOrders = whatsAppOrdersCombined;
        } else if (status === 'new') {
          targetOrders = pendingOrders;
        } else if (status === 'inProgress') {
          targetOrders = inProgressOrders;
        } else if (status === 'outForDelivery') {
          targetOrders = outForDeliveryOrders;
        } else if (status === 'delivered') {
          targetOrders = deliveredOrders;
        }

        // Sort merged orders by date descending
        targetOrders.sort((a, b) => new Date(b.createdOnUtc) - new Date(a.createdOnUtc));

        // 5. Apply search term filter if present
        let filtered = targetOrders;
        if (term) {
          const termLc = term.toLowerCase();
          filtered = targetOrders.filter(o => 
            (o.userName || '').toLowerCase().includes(termLc) || 
            (o.userPhone || '').toLowerCase().includes(termLc) ||
            (o.shippingAddress || '').toLowerCase().includes(termLc) ||
            (o.products || []).some(p => (p.productName || '').toLowerCase().includes(termLc))
          );
        }

        setAllMergedCount(filtered.length);
        setCurrentOrders(filtered.slice(currentPage * pageSize, (currentPage + 1) * pageSize));
      }
    } catch (err) {
      console.error("Error loading orders:", err);
    } finally {
      setIsLoadingOrders(false);
    }
  };


  useEffect(() => {
    loadDashboardCounts();
    loadSalesTrend();
  }, [activeStartDate, activeEndDate, vendorId]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      loadOrders(activeTab, searchTerm);
    }, 400);
    return () => clearTimeout(delayDebounce);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, activeTab, activeStartDate, activeEndDate, currentPage, pageSize, vendorId]);

  const handleTabChange = (tab) => {
    setCurrentPage(0);
    setActiveTab(tab);
  };

  const handleDateFilterChange = (filter) => {
    setCurrentPage(0);
    setDateFilter(filter);
  };

  const getEndDateLimits = () => {
    const todayStr = new Date().toLocaleDateString('sv-SE');
    if (!customStartDate) return { max: todayStr };
    return {
      min: customStartDate,
      max: todayStr
    };
  };

  const getStartDateLimits = () => {
    const todayStr = new Date().toLocaleDateString('sv-SE');
    if (!customEndDate) return { max: todayStr };
    return {
      max: customEndDate
    };
  };

  const handleCustomDateChange = (type, val) => {
    setCurrentPage(0);
    if (type === 'start') {
      setCustomStartDate(val);
      if (customEndDate && val > customEndDate) {
        setCustomEndDate(val);
      }
    } else {
      setCustomEndDate(val);
      if (customStartDate && val < customStartDate) {
        setCustomStartDate(val);
      }
    }
  };

  const handleChangeOrderStatus = async (orderId, statusId, tab) => {
    const res = await UpdateOrderStatus(orderId, statusId);
    if (res?.status) {
      setShowOrderModal(false);
      loadDashboardCounts();
      loadSalesTrend();
      handleTabChange(tab);
    } else {
      console.error(`Failed to update status for order ID: ${orderId}`);
    }
    return res;
  };

  const moveToNextStep = async (order, customStatusId = null) => {
    if (customStatusId) {
      return handleChangeOrderStatus(order.orderId, customStatusId, activeTab);
    }

    let tab = "";
    let statusId = 0;
    const status = order.orderStatus.toLowerCase();

    if (status === 'pending') {
      tab = 'inProgress'; statusId = 2;
    } else if (status === 'processing') {
      tab = 'outForDelivery'; statusId = 3;
    } else if (status === 'outfordelivery') {
      tab = 'delivered'; statusId = 4;
    }

    return handleChangeOrderStatus(order.orderId, statusId, tab);
  };

  const renderWithTooltip = (items) => {
    if (!items || items.length === 0) return <span className="text-muted">N/A</span>;

    const formatItem = (item) => {
      if (typeof item === 'object' && item !== null) {
        return item.qty ? `${item.name} (${item.qty})` : item.name;
      }
      return item;
    };

    if (!Array.isArray(items)) {
      return <span>{formatItem(items)}</span>;
    }

    const validItems = items.filter(Boolean);
    if (validItems.length === 0) return <span className="text-muted">N/A</span>;

    const first = formatItem(validItems[0]);
    const extras = validItems.slice(1);
    if (extras.length === 0) return <span>{first}</span>;

    return (
      <span className="grain-badge">
        {first}{' '}
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              {extras.map((item, idx) => (
                <div key={idx}>{formatItem(item)}</div>
              ))}
            </Tooltip>
          }
        >
          <span className="tooltip-badge">+{extras.length}</span>
        </OverlayTrigger>
      </span>
    );
  };

  const getSubProductNames = (order) => {
    try {
      return order.products.flatMap(p => (p.attributes?.subProducts || []).map(sp => sp.name));
    } catch (e) {
      return [];
    }
  };

  const mapSheetOrderToOrder = (row) => {
    const grainAddons = row.addOnGrains ? row.addOnGrains.split('|').map(s => s.trim()).filter(Boolean) : [];
    const grainQtys = row.addOnGrainsQty ? row.addOnGrainsQty.split('|').map(s => s.trim()) : [];
    const pulseAddons = row.addOnPulses ? row.addOnPulses.split('|').map(s => s.trim()).filter(Boolean) : [];
    const pulseQtys = row.addOnPulsesQty ? row.addOnPulsesQty.split('|').map(s => s.trim()) : [];

    const subProducts = [
      ...grainAddons.map((g, i) => ({ name: g, weight: grainQtys[i] || '' })),
      ...pulseAddons.map((p, i) => ({ name: p, weight: pulseQtys[i] || '' }))
    ];

    const attributes = [];
    if (row.grading) {
      attributes.push({ name: 'Grinding Texture', value: row.grading });
    }
    if (row.packaging) {
      attributes.push({ name: 'Packaging', value: row.packaging });
    }

    const parsedTotal = Number((row.total || '').toString().replace(/[^0-9.]/g, '')) || 0;
    const parsedWeight = Number((row.quantity || '').toString().replace(/[^0-9.]/g, '')) || 0;

    // Parse sheet date: handles "YYYY-MM-DD HH:MM:SS", "YYYY-MM-DD", "DD-MM-YYYY HH:MM:SS", "DD-MM-YYYY"
    let createdOnUtc = new Date().toISOString();
    if (row.date) {
      const s = row.date.toString().trim();
      const parts = s.split(' ');
      const dateStr = parts[0];
      const timeStr = parts[1] || '00:00:00';
      const dParts = dateStr.split(/[-/]/);

      if (dParts.length === 3) {
        let year, month, day;
        if (dParts[0].length === 4) {
          // YYYY-MM-DD
          year = dParts[0];
          month = dParts[1].padStart(2, '0');
          day = dParts[2].padStart(2, '0');
        } else if (dParts[2].length === 4) {
          // DD-MM-YYYY
          day = dParts[0].padStart(2, '0');
          month = dParts[1].padStart(2, '0');
          year = dParts[2];
        }
        if (year && month && day) {
          const parsed = new Date(`${year}-${month}-${day}T${timeStr}`);
          if (!isNaN(parsed.getTime())) {
            createdOnUtc = parsed.toISOString();
          }
        }
      }
    }

    return {
      orderId: row.orderId || 'N/A',
      userName: row.customerName || 'N/A',
      userPhone: row.phone || '',
      shippingAddress: row.address || '',
      createdOnUtc: createdOnUtc,
      deliveryDate: '',
      weight: parsedWeight,
      totalPrice: parsedTotal,
      paymentStatus: row.status || 'Pending',
      orderStatus: row.status || 'Pending',
      paymentMethod: 'WhatsApp',
      products: [
        {
          productName: row.product || 'N/A',
          price: parsedTotal,
          weight: parsedWeight,
          attributes: {
            product: [{ name: row.product || 'N/A', weight: row.quantity || '' }],
            subProducts: subProducts,
            attributes: attributes
          }
        }
      ]
    };
  };

  const formatWeight = (weight) => {
    if (!weight || weight === 0) return 'N/A';
    return `${weight} Kg`;
  };

  const getTabTotalCount = () => {
    switch (activeTab) {
      case 'all': return tableTabCount.allOrdersCount;
      case 'new': return tableTabCount.newOrdersCount;
      case 'inProgress': return tableTabCount.inProgressCount;
      case 'outForDelivery': return tableTabCount.outForDeliveryCount;
      case 'delivered': return tableTabCount.deliveredCount;
      case 'whatsapp': return tableTabCount.whatsAppOrdersCount;
      default: return 0;
    }
  };

  const totalOrders = getTabTotalCount();
  const totalPages = Math.ceil(totalOrders / pageSize) || 1;

  const getBtnStyle = (filter) => ({
    backgroundColor: dateFilter === filter ? "#5d4037" : "transparent",
    color: dateFilter === filter ? "#fff" : "#5d4037",
    border: "1px solid #5d4037",
    borderRadius: "20px",
    padding: "6px 16px",
    fontSize: "13px",
    fontWeight: "600",
    transition: "all 0.2s ease"
  });

  return (
    <div className="dashboard mt-5">
      {/* --- Header --- */}
      <div className="d-flex justify-content-between align-items-center mt-4 mb-4">
        <div>
          <h2>Hey {userName}! 👋</h2>
          <p><small>Welcome back to your dashboard.</small></p>
        </div>
        <button className="add-product-btn" onClick={() => setShowSelfOrderModal(true)}>+ CREATE SELF ORDER</button>
      </div>

      {/* --- Date Presets & Custom Filter Bar --- */}
      <div className="d-flex gap-2 mb-5 align-items-center flex-wrap" style={{ backgroundColor: "#fdfcfb", padding: "12px 20px", borderRadius: "10px", border: "1px solid #efebe9" }}>
        <span style={{ fontWeight: "600", fontSize: "14px", color: "#5d4037", marginRight: "10px" }}>Filters:</span>
        <button 
          style={getBtnStyle('all')}
          onClick={() => handleDateFilterChange('all')}
        >
          All
        </button>
        <button 
          style={getBtnStyle('today')}
          onClick={() => handleDateFilterChange('today')}
        >
          Today
        </button>
        <button 
          style={getBtnStyle('yesterday')}
          onClick={() => handleDateFilterChange('yesterday')}
        >
          Yesterday
        </button>
        <button 
          style={getBtnStyle('thisWeek')}
          onClick={() => handleDateFilterChange('thisWeek')}
        >
          This Week
        </button>
        <button 
          style={getBtnStyle('custom')}
          onClick={() => handleDateFilterChange('custom')}
        >
          Custom Date Range
        </button>

        {dateFilter === 'custom' && (
          <div className="d-flex align-items-center gap-2 ms-md-auto">
            <input 
              type="date" 
              className="form-control form-control-sm" 
              value={customStartDate} 
              onChange={(e) => handleCustomDateChange('start', e.target.value)} 
              min={getStartDateLimits().min}
              max={getStartDateLimits().max}
              style={{ width: "150px" }}
            />
            <span style={{ fontSize: "13px", color: "#795548", fontWeight: "600" }}>to</span>
            <input 
              type="date" 
              className="form-control form-control-sm" 
              value={customEndDate} 
              onChange={(e) => handleCustomDateChange('end', e.target.value)} 
              min={getEndDateLimits().min}
              max={getEndDateLimits().max}
              style={{ width: "150px" }}
            />
          </div>
        )}
      </div>      {/* --- Sales Trend & Stats Cards --- */}
      <div className="d-flex mt-4 align-items-stretch gap-4">
        <div className="sales-trend">
          <div className="sales-header d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <strong className="sales-title">Sales Trend</strong>
              <small className="text-muted" style={{ fontSize: '12px' }}>
                {dateFilter === 'all' ? "Last 7 Days" : dateFilter === 'custom' ? "Custom Range" : dateFilter}
              </small>
            </div>
            <div className="d-flex align-items-center gap-2">
              {/* Clickable Legend Toggles */}
              <button
                onClick={() => setChartFilter(chartFilter === 'website' ? 'both' : 'website')}
                title="Toggle Website sales"
                style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  fontSize: '11px', fontWeight: '600', border: '1.5px solid #5d4037',
                  borderRadius: '20px', padding: '2px 9px', cursor: 'pointer',
                  background: (chartFilter === 'both' || chartFilter === 'website') ? '#5d4037' : 'transparent',
                  color: (chartFilter === 'both' || chartFilter === 'website') ? '#fff' : '#5d4037',
                  transition: 'all 0.2s',
                }}
              >
                <span style={{ width: '7px', height: '7px', borderRadius: '2px', background: (chartFilter === 'both' || chartFilter === 'website') ? '#fff' : '#5d4037', flexShrink: 0 }}></span>
                Website
              </button>
              <button
                onClick={() => setChartFilter(chartFilter === 'whatsapp' ? 'both' : 'whatsapp')}
                title="Toggle WhatsApp sales"
                style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  fontSize: '11px', fontWeight: '600', border: '1.5px solid #25D366',
                  borderRadius: '20px', padding: '3px 9px', cursor: 'pointer',
                  background: (chartFilter === 'both' || chartFilter === 'whatsapp') ? '#25D366' : 'transparent',
                  color: (chartFilter === 'both' || chartFilter === 'whatsapp') ? '#fff' : '#25D366',
                  transition: 'all 0.2s',
                }}
              >
                <span style={{ width: '7px', height: '7px', borderRadius: '2px', background: (chartFilter === 'both' || chartFilter === 'whatsapp') ? '#fff' : '#25D366', flexShrink: 0 }}></span>
                WhatsApp
              </button>
              <img src={filter_icon} alt="Filter" className="filter-icon" title="Filter" />
            </div>
          </div>

          {/* Chart body */}
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'flex-end', marginTop: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', width: '100%', height: '140px' }}>
              {(() => {
                const showWebsite = chartFilter === 'both' || chartFilter === 'website';
                const showWp = chartFilter === 'both' || chartFilter === 'whatsapp';
                const maxSales = Math.max(
                  ...salesTrend.map(d => {
                    let m = 0;
                    if (showWebsite) m = Math.max(m, d.value || 0);
                    if (showWp) m = Math.max(m, d.wpValue || 0);
                    return m;
                  }),
                  1
                );
                return salesTrend.map(({ day, date, value, wpValue = 0 }) => {
                  const barH = showWebsite && value > 0 ? Math.max(6, (value / maxSales) * 100) : 0;
                  const wpH = showWp && wpValue > 0 ? Math.max(6, (wpValue / maxSales) * 100) : 0;

                  return (
                    <div key={date} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                      {/* Bars pair container */}
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '110px' }}>

                        {/* Website bar */}
                        {showWebsite && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tw-${date}`}>
                                <strong>{date} ({day})</strong><br />
                                Website: ₹{value.toLocaleString('en-IN')}
                              </Tooltip>
                            }
                          >
                            <div style={{
                              width: '10px',
                              height: barH > 0 ? `${barH}px` : '0px',
                              background: 'linear-gradient(180deg, #8d6e63, #5d4037)',
                              borderRadius: '3px 3px 0 0',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              opacity: barH > 0 ? 1 : 0,
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                            />
                          </OverlayTrigger>
                        )}

                        {/* WhatsApp bar */}
                        {showWp && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`twp-${date}`}>
                                <strong>{date} ({day})</strong><br />
                                WhatsApp: ₹{wpValue.toLocaleString('en-IN')}
                              </Tooltip>
                            }
                          >
                            <div style={{
                              width: '10px',
                              height: wpH > 0 ? `${wpH}px` : '0px',
                              background: 'linear-gradient(180deg, #66bb6a, #25D366)',
                              borderRadius: '3px 3px 0 0',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              opacity: wpH > 0 ? 1 : 0,
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                            />
                          </OverlayTrigger>
                        )}

                      </div>
                      {/* Day label */}
                      <div style={{ fontSize: '11px', fontWeight: '600', color: '#795548', marginTop: '6px', textAlign: 'center' }} title={date}>{day}</div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>

        </div>

        {/* --- STATS CARDS --- */}
        <div className="stats flex-grow-1 d-flex gap-4" style={{ margin: 0 }}>
          <div className="stat-card" onClick={() => navigate('/customers')} style={{ cursor: 'pointer' }}>

            <div className="stat-icon">
              <img src={usersIcon} alt="Users" className="icon-img mb-3" />
            </div>
            <div className="stat-title">
              {dateFilter === 'all' ? "Total" : "New"}<br />Customers
            </div>
            <div className="number">{counts.userCount || 0}</div>
            <div className="arrow-icon">↗</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <img src={productsIcon} alt="Products" className="icon-img mb-3" />
            </div>
            <div className="stat-title">Total<br />Products</div>
            <div className="number">{counts.productCount || 0}</div>
            <div className="arrow-icon">↗</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <img src={ordersIcon} alt="Orders" className="icon-img mb-3" />
            </div>
            <div className="stat-title">Completed<br />Orders</div>
            <div className="number">{counts.orderCount || 0}</div>
            <div className="arrow-icon">↗</div>
          </div>
        </div>
      </div>

      {/* --- Order List & Tabs --- */}
      <div className="order-list">
        <div className="order-header">
          {/* --- TABS --- */}
          <div className="tabs">
            <button className={activeTab === 'all' ? 'active' : ''} onClick={() => handleTabChange('all')}>
              All ({tableTabCount.allOrdersCount || 0})
            </button>
            <button className={activeTab === 'website' ? 'active' : ''} onClick={() => handleTabChange('website')}>
              Website ({tableTabCount.websiteOrdersCount || 0})
            </button>
            <button className={activeTab === 'whatsapp' ? 'active' : ''} onClick={() => handleTabChange('whatsapp')}>
              WhatsApp ({tableTabCount.whatsAppOrdersCount || 0})
            </button>
            <button className={activeTab === 'new' ? 'active' : ''} onClick={() => handleTabChange('new')}>
              Pending ({tableTabCount.newOrdersCount || 0})
            </button>
            <button className={activeTab === 'inProgress' ? 'active' : ''} onClick={() => handleTabChange('inProgress')}>
              In Progress ({tableTabCount.inProgressCount || 0})
            </button>
            <button className={activeTab === 'outForDelivery' ? 'active' : ''} onClick={() => handleTabChange('outForDelivery')}>
              Out For Delivery ({tableTabCount.outForDeliveryCount || 0})
            </button>
            <button className={activeTab === 'delivered' ? 'active' : ''} onClick={() => handleTabChange('delivered')}>
              Delivered ({tableTabCount.deliveredCount || 0})
            </button>
          </div>

          {/* --- Search --- */}
          <div className="search-tools">
            <div className="search-wrapper">
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search Order"
              />
              <div className="icons">
                <img src={search_icon} alt="Search" className="search-icon" />
                <img src={brownfilter_icon} alt="Filter" className="filter-icon" />
              </div>
            </div>
          </div>
        </div>

        {/* --- Order Table --- */}
        <div className="order-table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Customer Details</th>
                <th>Order Items</th>
                <th>Delivery Location</th>
                <th>Placed On</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingOrders ? (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    Loading orders...
                  </td>
                </tr>
              ) : currentOrders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="no-orders text-center">No orders found.</td>
                </tr>
              ) : (
                currentOrders.map((order, idx) => {
                  const statusLc = (order.orderStatus || '').toLowerCase();
                  const statusBg = statusLc === 'completed' || statusLc === 'delivered' || statusLc === 'paid' ? '#e8f5e9'
                    : statusLc === 'pending' || statusLc === 'new' ? '#fff3e0'
                    : statusLc === 'processing' || statusLc === 'in progress' || statusLc === 'inprogress' ? '#e3f2fd' : '#fafafa';
                  const statusColor = statusLc === 'completed' || statusLc === 'delivered' || statusLc === 'paid' ? '#2e7d32'
                    : statusLc === 'pending' || statusLc === 'new' ? '#ef6c00'
                    : statusLc === 'processing' || statusLc === 'in progress' || statusLc === 'inprogress' ? '#1565c0' : '#616161';
                  
                  const whatsAppLink = `https://wa.me/${(order.userPhone || '').replace(/[^0-9]/g, '')}`;
                  const googleMapsLink = order.shippingAddress && (order.shippingAddress.startsWith('http') || order.shippingAddress.includes('maps?q=')) 
                    ? order.shippingAddress 
                    : '';
                  const isAddressUrl = !!googleMapsLink;

                  return (
                    <tr key={order.orderId || idx}>
                      {/* Customer */}
                      <td>
                        <div className="customer-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <img src={usersIcon} alt={order.userName} className="customer-img" />
                            <span style={{ fontWeight: '600' }}>{order.userName || 'N/A'}</span>
                          </div>
                          {order.userPhone && (
                            <a
                              href={whatsAppLink}
                              target="_blank"
                              rel="noreferrer"
                              style={{ fontSize: '12px', color: '#25D366', marginLeft: '32px', textDecoration: 'none' }}
                            >
                              {order.userPhone}
                            </a>
                          )}
                        </div>
                      </td>
                      {/* Order Items */}
                      <td>
                        <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                          {order.products?.map((p, pIdx) => {
                            const productName = p.productName || 'N/A';
                            const wheatVariant = p.attributes?.product?.[0]?.name;
                            const wheatWeight = p.attributes?.product?.[0]?.weight;
                            const attrs = (p.attributes?.attributes || []).map(a => a.value).filter(Boolean);
                            const addons = (p.attributes?.subProducts || []);
                            const totalWeight = p.weight || p.quantity || 0;
                            return (
                              <div key={pIdx} style={{ marginBottom: pIdx < order.products.length - 1 ? '8px' : 0 }}>
                                <div style={{ fontWeight: '600', color: '#3e2723' }}>{productName}</div>
                                {wheatVariant && wheatVariant !== productName && (
                                  <div style={{ color: '#795548', fontSize: '11px' }}>
                                    Wheat: {wheatVariant}{wheatWeight ? ` (${wheatWeight})` : ''}
                                  </div>
                                )}
                                {attrs.length > 0 && (
                                  <div style={{ color: '#8d6e63', fontSize: '11px' }}>{attrs.join(' · ')}</div>
                                )}
                                {addons.length > 0 && (
                                  <div style={{ color: '#666', fontSize: '11px' }}>+ {addons.map(sp => `${sp.name} ${sp.weight || ''}`.trim()).join(', ')}</div>
                                )}
                                <div style={{ color: '#888', fontSize: '11px', marginTop: '2px' }}>{totalWeight} Kg · ₹{p.price}</div>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      {/* Location */}
                      <td>
                        <div style={{ fontSize: '12px' }}>
                          {order.shippingAddress && !isAddressUrl ? (
                            <span style={{ display: 'inline-block', maxWidth: '180px', whiteSpace: 'normal', wordBreak: 'break-word' }}>{order.shippingAddress}</span>
                          ) : null}
                          {isAddressUrl ? (
                            <a
                              href={order.shippingAddress}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: '#1976D2', fontWeight: '600', textDecoration: 'none', display: 'block' }}
                            >
                              📍 View Location
                            </a>
                          ) : !order.shippingAddress && order.userPhone ? (
                            <a
                              href={whatsAppLink}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: '#25D366', fontWeight: '600', textDecoration: 'none' }}
                            >
                              📱 {order.userPhone}
                            </a>
                          ) : null}
                        </div>
                      </td>
                      {/* Date */}
                      <td style={{ fontSize: '12px' }}>{new Date(order.createdOnUtc).toLocaleString()}</td>
                      {/* Amount */}
                      <td style={{ fontWeight: '600', color: '#3e2723' }}>
                        ₹ {Number(order.totalPrice).toLocaleString('en-IN')}
                      </td>
                      {/* Status */}
                      <td>
                        <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', display: 'inline-block', backgroundColor: statusBg, color: statusColor }}>
                          {order.orderStatus || 'Pending'}
                        </span>
                      </td>
                      {/* Action */}
                      <td className="action-icon">
                        <img
                          src={eyeIcon}
                          alt="View Details"
                          title="View Details"
                          className="eye-icon"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowOrderModal(true);
                          }}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>

          </table>
        </div>

        {/* --- Pagination Controls --- */}
        <div className="d-flex justify-content-between align-items-center mt-3 px-3 py-2" style={{ borderTop: "1px solid #efebe9" }}>
          <small style={{ color: "#795548", fontWeight: "500" }}>
            Showing {totalOrders > 0 ? currentPage * pageSize + 1 : 0} to {Math.min((currentPage + 1) * pageSize, totalOrders)} of {totalOrders} orders
          </small>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={currentPage === 0}
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              style={{
                color: currentPage === 0 ? "#aaa" : "#5d4037",
                borderColor: currentPage === 0 ? "#ddd" : "#5d4037",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: "600",
                cursor: currentPage === 0 ? "not-allowed" : "pointer"
              }}
            >
              Previous
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={currentPage >= totalPages - 1}
              onClick={() => setCurrentPage(prev => prev + 1)}
              style={{
                color: currentPage >= totalPages - 1 ? "#aaa" : "#5d4037",
                borderColor: currentPage >= totalPages - 1 ? "#ddd" : "#5d4037",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: "600",
                cursor: currentPage >= totalPages - 1 ? "not-allowed" : "pointer"
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* --- Modals --- */}
      {showPopup && <Addproduct onClose={() => { setShowPopup(false); }} />}

      <AddSelfOrderModal
        show={showSelfOrderModal}
        handleClose={() => setShowSelfOrderModal(false)}
        onSuccess={() => {
          setShowSelfOrderModal(false);
          loadDashboardCounts();
          loadSalesTrend();
          loadOrders(activeTab, searchTerm);
        }}
      />

      <OrderDetailsModal
        show={showOrderModal}
        handleClose={() => setShowOrderModal(false)}
        order={selectedOrder}
        moveToNextStep={moveToNextStep}
      />
    </div>
  );
}